import { getLogger } from '@logtape/logtape';
import type { ConfigData } from '#lib/apps/types.js';
import { getDataClient, localDatabaseExists } from '#data/safesystem/safecurves/factory.js';
import { activeProgramsVersion, programsEnabled } from './gate';
import {
	buildProgramsMapFeatures,
	type ProgramsMapFeatures,
	type ProjectRows
} from './mapFeatures';

const log = getLogger(['safesystem', 'safecurves', 'programs', 'map']);

const EMPTY: ProgramsMapFeatures = buildProgramsMapFeatures([]);

// The active version's projects plus their existing signs and barriers, for the main SafeCurves map and table.
class ProgramsMapStore {
	features = $state<ProgramsMapFeatures>(EMPTY);
	// Gates the Programs legend rows and table tabs.
	hasProjects = $state(false);
	#loadId = 0;

	// Never creates the local DB: a device that has never opened Programs has nothing to show.
	async load(configData: ConfigData): Promise<void> {
		const loadId = ++this.#loadId;
		const version = activeProgramsVersion();
		let rows: ProjectRows[] = [];
		try {
			if (version && programsEnabled(configData) && (await localDatabaseExists()))
				rows = await readRows(version);
		} catch (error) {
			log.error('Failed to load Programs map data: {error}', { error });
		}
		if (loadId !== this.#loadId) return;
		this.features = buildProgramsMapFeatures(rows);
		this.hasProjects = rows.length > 0;
		log.info('Loaded {count} projects for the map', { count: rows.length });
	}
}

async function readRows(version: string): Promise<ProjectRows[]> {
	const client = await getDataClient();
	const programs = await client.listPrograms(version);
	const perProgram = await Promise.all(
		programs.map(async (program) =>
			Promise.all(
				(await client.listProjects(program.id)).map(async (project) => {
					const [corridors, curves, signs, signApps, barriers, barrierApps] = await Promise.all([
						client.listCorridors(project.id),
						client.listProjectCurves(project.id),
						client.listProjectSigns(project.id),
						client.listCountermeasureApplications(project.id, 'sign'),
						client.listProjectBarriers(project.id),
						client.listCountermeasureApplications(project.id, 'barrier')
					]);
					return { program, project, corridors, curves, signs, signApps, barriers, barrierApps };
				})
			)
		)
	);
	return perProgram.flat();
}

export const programsMapStore = new ProgramsMapStore();
